import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Popover, TextField } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dayjs from "dayjs";

import { PopoverContent } from "./PopoverContent";

interface Props {
  onChange?: (data: { start: string; end: string } | null) => void;
  startDate?: string | null;
  endDate?: string | null;
  label?: string;
}

const regExps = [
  /^[qQ]$/,
  /^[qQ][1-4]$/,
  /^[qQ][1-4] $/, // Specific case - autofill
  /^[qQ][1-4] -$/, // Specific case - autofill
  /^[qQ][1-4] - $/, // Specific case - autofill
  /^[qQ][1-4] - \d{1}$/,
  /^[qQ][1-4] - \d{2}$/,
  /^[qQ][1-4] - \d{3}$/,
  /^[qQ][1-4] - \d{4}$/
];

const quartersPoints: { start: string; end: string }[] = [
  { start: "01-01", end: "03-31" },
  { start: "04-01", end: "06-30" },
  { start: "07-01", end: "09-30" },
  { start: "10-01", end: "12-31" }
];

export const years = new Array(200).fill(1900).map((year, index) => year + index);
const midday = "T12:00:00";

const placeholder = `Q1 - ${dayjs().year()}`;

export const DateQuarterPicker: React.FC<Props> = ({ onChange, startDate, endDate, label }) => {
  const [inputValue, setInputValue] = useState<string>("");

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const isFirstRender: React.MutableRefObject<boolean> = useRef(true);

  const ref = useRef<HTMLDivElement | null>(null);

  const handleOpenPopup = () => setIsOpen(true);
  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSelect = (year: number, quarter: number) => {
    setInputValue(`Q${quarter} - ${year}`);
  };

  const quarterNumber: number | null = useMemo(() => {
    if (!inputValue) return null;
    return Number(inputValue.slice(1, 2));
  }, [inputValue]);

  const yearNumber: number = useMemo(() => {
    if (!inputValue) return dayjs().year();
    return Number(inputValue.slice(5));
  }, [inputValue]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (quarterNumber && years.includes(yearNumber)) {
      onChange?.({
        start: `${yearNumber}-${quartersPoints[quarterNumber - 1].start}${midday}Z`,
        end: `${yearNumber}-${quartersPoints[quarterNumber - 1].end}${midday}Z`
      });
    } else {
      onChange?.(null);
    }
  }, [inputValue]);

  // Reset input value when startDate or endDate changes
  useEffect(() => {
    if (!startDate || !endDate) return;

    const startQuarter = dayjs(startDate).format("YYYY-MM-DD").slice(5);
    const endQuarter = dayjs(endDate).format("YYYY-MM-DD").slice(5);

    const year = dayjs(startDate).format("YYYY");

    const quarterIndex = quartersPoints.findIndex(
      (quarter) => quarter.start === startQuarter && quarter.end === endQuarter
    );

    if (quarterIndex === -1) return;

    const newInputValue = `Q${quarterIndex + 1} - ${year}`;

    if (newInputValue === inputValue) return;
    setInputValue(newInputValue);
  }, [startDate, endDate]);

  return (
    <>
      <Box sx={{ position: "relative" }}>
        <TextField
          ref={ref}
          onClick={handleOpenPopup}
          sx={{ zIndex: 1 }}
          value={inputValue}
          fullWidth
          label={label}
          onPaste={(e) => e.preventDefault()}
          onChange={(e) => {
            let value = e.target.value.toUpperCase();
            const valueLength = value.length;
            const enteredYear = Number(value.slice(5));

            // In case of clear;
            if (!value) return setInputValue("");

            // Maximum string length
            if (value.length > 9) return e;

            const quarterRegExp = new RegExp(regExps[valueLength - 1]);

            if (!quarterRegExp.test(value)) return e;

            // Autofill " - "
            if (valueLength > 2 && valueLength <= 5 && inputValue.length === 2) {
              value = `${value.slice(0, 2)} - `;
            }

            // Auto remove " - "
            if (valueLength > 2 && valueLength <= 5 && inputValue.length === 5) {
              value = `${value.slice(0, 2)}`;
            }

            // Limit year
            if (valueLength === 9) {
              if (enteredYear < years[0]) {
                value = `${value.slice(0, 5)}${years[0]}`;
              }
              if (enteredYear > years[years.length - 1]) {
                value = `${value.slice(0, 5)}${years[years.length - 1]}`;
              }
            }

            setInputValue(value);
            return e;
          }}
          slotProps={{
            input: {
              endAdornment: (
                <IconButton sx={{ mr: "-10px" }} onClick={handleOpenPopup}>
                  <CalendarMonthIcon />
                </IconButton>
              )
            }
          }}
        />
        <TextField
          sx={{
            width: "100%",
            position: "absolute",
            left: 0,
            zIndex: 0
          }}
          disabled
          label={label}
          value={`${inputValue}${placeholder.slice(inputValue.length)}`}
        />
      </Box>

      <Popover
        anchorEl={ref?.current}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
      >
        <PopoverContent
          handleClose={handleClose}
          onSelect={handleSelect}
          quarterNumber={quarterNumber}
          yearNumber={yearNumber}
        />
      </Popover>
    </>
  );
};
